var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useAdvancedApprovalState } from 'app/domains/approval';
import { useCreateAssistantStream } from 'app/domains/assistant/hooks/useCreateAssistantStream';
import { isApolloError } from 'shared/domains/apollo/isApolloError';
import { useActiveDocument } from 'shared/domains/documents';
import { CreateApprovalCheckChatDocument } from './CreateApprovalCheckChat.gql';
import { SendApprovalCheckPromptDocument } from './SendApprovalCheckPrompt.gql';
import { SendApprovalCheckWorkflowDocument } from './SendApprovalCheckWorkflow.gql';
import { SyncAssistantCreditsDocument } from './SyncAssistantCredits.gql';
import { zDocumentApprovalResultSchema } from './schemas';
import { ApprovalCheckErrorSubcodes } from './types';
export const useStreamApprovalCheck = () => {
    const { id: documentId } = useActiveDocument();
    const { response: initialResponse, setDocumentState } = useAdvancedApprovalState(documentId);
    const createEventSource = useCreateAssistantStream();
    const [createChatMutation] = useMutation(CreateApprovalCheckChatDocument);
    const [sendPromptMutation] = useMutation(SendApprovalCheckPromptDocument);
    const [sendWorkflowMutation] = useMutation(SendApprovalCheckWorkflowDocument);
    const [syncCreditsQuery] = useLazyQuery(SyncAssistantCreditsDocument);
    const [state, setState] = useState(initialResponse ? 'done' : 'initial');
    const [error, setError] = useState(null);
    const [response, setResponse] = useState(initialResponse || {
        summary: '',
        isApprovalRequired: false,
        approvers: [],
    });
    const handleStreamResponse = (_a) => __awaiter(void 0, [_a], void 0, function* ({ chatId, streamToken }) {
        const evtSource = yield createEventSource(streamToken, chatId, 'document');
        const closeStream = () => {
            evtSource.close();
            setState('done');
        };
        evtSource.onmessage = (event) => {
            const [, [kind, payload]] = JSON.parse(event.data);
            if (kind === 'END') {
                closeStream();
                return;
            }
            if (kind === 'ERROR') {
                setError('streaming');
                closeStream();
                return;
            }
            const { success, data } = zDocumentApprovalResultSchema.safeParse(JSON.parse(payload));
            if (success) {
                setResponse(data);
                setDocumentState({ skipped: true, response: data });
            }
            else {
                setError('streaming');
                closeStream();
            }
        };
        // The server will close the connection after sending the end token, this is treated as
        // an error state by the EventSource, and we thus close the connection.
        evtSource.onerror = () => {
            closeStream();
        };
    });
    const resetApprovalCheck = () => {
        setError(null);
        setState('initial');
        setResponse({ summary: '', isApprovalRequired: false, approvers: [] });
    };
    const createChat = (context) => __awaiter(void 0, void 0, void 0, function* () {
        const { data } = yield createChatMutation({ variables: { documentId } });
        const chatId = data === null || data === void 0 ? void 0 : data.createDocumentChat.id;
        if (!chatId) {
            throw new Error('Failed to create chat');
        }
        return { chatId, context };
    });
    const handleError = (err) => {
        var _a, _b;
        setState('initial');
        if (isApolloError(err)) {
            const subcode = (_b = (_a = err.graphQLErrors[0]) === null || _a === void 0 ? void 0 : _a.extensions) === null || _b === void 0 ? void 0 : _b.subcode;
            if (subcode === ApprovalCheckErrorSubcodes.InsufficientCredits) {
                syncCreditsQuery();
                setError('credits');
            }
        }
        else {
            setError('setup');
        }
    };
    const sendPrompt = (_b) => __awaiter(void 0, [_b], void 0, function* ({ chatId, promptId, context }) {
        var _c;
        const { data } = yield sendPromptMutation({ variables: { chatId, promptId, context } });
        const streamToken = (_c = data === null || data === void 0 ? void 0 : data.lastChat.messages[0]) === null || _c === void 0 ? void 0 : _c.streamToken;
        if (!streamToken) {
            throw new Error('Failed to send prompt');
        }
        return { chatId, streamToken };
    });
    const sendWorkflow = (_d) => __awaiter(void 0, [_d], void 0, function* ({ chatId, workflowId, context, }) {
        var _e;
        const { data } = yield sendWorkflowMutation({ variables: { chatId, workflowId, context } });
        const streamToken = (_e = data === null || data === void 0 ? void 0 : data.lastChat.messages[0]) === null || _e === void 0 ? void 0 : _e.streamToken;
        if (!streamToken) {
            throw new Error('Failed to send workflow');
        }
        return { chatId, streamToken };
    });
    const startApprovalCheck = (promptId, context) => __awaiter(void 0, void 0, void 0, function* () {
        setState('evaluating');
        yield createChat(context)
            .then(({ chatId, context: ctx }) => sendPrompt({ chatId, promptId, context: ctx }))
            .then(handleStreamResponse)
            .catch(handleError);
    });
    const startApprovalCheckWithWorkflow = (workflowId, context) => __awaiter(void 0, void 0, void 0, function* () {
        setState('evaluating');
        yield createChat(context)
            .then(({ chatId, context: ctx }) => sendWorkflow({ chatId, workflowId, context: ctx }))
            .then(handleStreamResponse)
            .catch(handleError);
    });
    return { startApprovalCheck, startApprovalCheckWithWorkflow, resetApprovalCheck, state, response, error };
};
